import { useCallback, useEffect, useState } from 'react';
import { supabase, SUPABASE_URL } from '@/src/lib/supabase';

export type MetricsPeriod = 'week' | 'last_week' | 'month' | 'last_month' | '30d' | 'custom';
export interface DateRange { from: string; to: string }

export interface XeroMetrics {
  connected: boolean;
  error?: string;
  org?: string | null;
  revenue?: number | null;
  expenses?: number | null;
  net?: number | null;
  unpaid?: { count: number; total: number };
  overdue?: { count: number; total: number };
  topSuppliers?: Array<{ name: string; total: number }>;
  trend?: Array<{ month: string; revenue: number; expenses: number }>;
}
export interface DeputyMetrics {
  connected: boolean;
  error?: string;
  lastSynced?: string | null;
  hours?: number;
  cost?: number | null;
  staffToday?: number;
  shiftsToday?: number;
  byDay?: Array<{ day: string; date: string; hours: number }>;
  hoursToDate?: number;
  costToDate?: number | null;
  series?: Array<{ date: string; hours: number; cost: number }>;
  coverage?: Coverage;
}
export interface SquareMetrics {
  connected: boolean;
  error?: string;
  sales?: number;
  orders?: number;
  avgOrder?: number | null;
  today?: { sales: number; orders: number };
  byDay?: Array<{ day: string; date: string; sales: number }>;
  topItems?: Array<{ name: string; qty: number; revenue: number }>;
  note?: string;
  /** 'ordermate' when sales come from imported OrderMate data instead of Square. */
  source?: 'square' | 'ordermate';
  lastDate?: string | null;
  foodSales?: number;
  bevSales?: number;
  series?: Array<{ date: string; sales: number; orders: number }>;
  prevSeries?: Array<{ date: string; sales: number }>;
  coverage?: Coverage;
}
export interface Coverage { days: number; daysWithData: number; first: string | null; last: string | null }
export interface MetricsCompare {
  from: string; to: string; label: string;
  sales: number | null; orders: number | null; labourCost: number | null; hours: number | null; labourPct: number | null;
}
export interface OwnerMetrics {
  ok: boolean;
  currency: string;
  period: { key: MetricsPeriod; from: string; to: string; label: string };
  computedAt: string;
  xero: XeroMetrics;
  deputy: DeputyMetrics;
  labourPct: number | null;
  square?: SquareMetrics;
  labourPctSource?: 'square' | 'ordermate' | 'xero' | null;
  cached?: boolean;
  compare?: MetricsCompare;
}

export function useOwnerMetrics(venueId: string | null, period: MetricsPeriod, enabled = true, range?: DateRange | null) {
  const from = period === 'custom' ? range?.from ?? null : null;
  const to = period === 'custom' ? range?.to ?? null : null;
  const [data, setData] = useState<OwnerMetrics | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (refresh = false) => {
    if (!enabled || !venueId || (period === 'custom' && (!from || !to))) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data: s } = await supabase.auth.getSession();
      if (!s.session) { setError('Not signed in'); setLoading(false); return; }
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/owner-metrics?forceFunctionRegion=ap-southeast-2`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${s.session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ period, refresh, ...(from && to ? { from, to } : {}) }),
      });
      const body = (await resp.json()) as OwnerMetrics & { error?: string };
      if (!resp.ok || !body.ok) setError(body.error ?? `Error ${resp.status}`);
      else setData(body);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
    setLoading(false);
  }, [venueId, period, enabled, from, to]);

  useEffect(() => { void load(); }, [load]);
    // Refetch fresh numbers when something changes the underlying data (e.g. a Deputy sync).
    useEffect(() => {
        const onStale = () => { void load(true); };
        window.addEventListener('opsalo:metrics-stale', onStale);
        return () => window.removeEventListener('opsalo:metrics-stale', onStale);
      }, [load]);

  return { data, loading, error, refresh: () => load(true) };
}